import { Banner, Button } from '@neo4j-ndl/react';
import { MagnifyingGlassIconSolid, PlusIconOutline } from '@neo4j-ndl/react/icons';
import { MODAL_TYPE, useConnection, useConnections, useConnectionsActions, useModal } from '@nx/state';
import { EmptyState, ExternalDataSourceCard, ExternalDataSourceEditor, SearchField } from '@nx/ui';
import { useMemo, useState } from 'react';

function Highlighter(props: { substring: string; value: string }) {
  if (props.substring === '') {
    return <span>{props.value}</span>;
  }

  const startIndex = props.value.toLowerCase().indexOf(props.substring.toLowerCase());

  if (startIndex < 0) {
    return <span>{props.value}</span>;
  }

  const pre = props.value.slice(0, startIndex);
  const match = props.value.slice(startIndex, startIndex + props.substring.length);
  const post = props.value.slice(startIndex + props.substring.length);

  return (
    <span>
      {pre}
      <mark className="bg-palette-primary-bg-weak text-palette-primary-text">{match}</mark>
      {post}
    </span>
  );
}

export function ExternalDataSources(props: { allowEdit?: boolean }) {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [filterQuery, setFilterQuery] = useState('');
  const activeConnection = useConnection();
  const connectionFormModal = useModal(MODAL_TYPE.CONNECTION_FORM);
  const connections = useConnections();
  const connectionsActions = useConnectionsActions();
  const externalConnections = connections.filter((connection) => {
    return connection.source.type === 'user';
  });

  const normalizedFilterQuery = filterQuery.trim().toLocaleLowerCase();
  const filteredConnections = useMemo(() => {
    if (normalizedFilterQuery === '') {
      return externalConnections;
    }

    return externalConnections.filter((connection) => {
      const normalizedConnectionName = connection.name.toLocaleLowerCase();
      return normalizedConnectionName.includes(normalizedFilterQuery);
    });
  }, [externalConnections, normalizedFilterQuery]);

  if (externalConnections.length === 0) {
    return (
      <EmptyState
        icon="remote-database"
        title="No connections found"
        description="Connect to an instance to get started"
      >
        <Button
          fill="text"
          onClick={() =>
            connectionFormModal.open({
              error: undefined,
              hideConnectionMethods: undefined,
              initialValues: undefined,
              instanceDetails: undefined,
              source: { type: 'user' },
              warning: undefined,
            })
          }
        >
          <PlusIconOutline className="h-4 w-4" /> New connection
        </Button>
      </EmptyState>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex">
        <SearchField
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          htmlAttributes={{ 'aria-label': 'Search for instances' }}
        />
        <div className="ml-auto">
          <Button
            fill="outlined"
            onClick={() =>
              connectionFormModal.open({
                error: undefined,
                hideConnectionMethods: undefined,
                initialValues: undefined,
                instanceDetails: undefined,
                source: { type: 'user' },
                warning: undefined,
              })
            }
          >
            <PlusIconOutline className="h-4 w-4" /> New connection
          </Button>
        </div>
      </div>
      <div className="space-y-4">
        {filteredConnections.length === 0 ? (
          <EmptyState
            customIcon={<MagnifyingGlassIconSolid className="h-12 w-12" />}
            title="No matching connections found"
            description="Please review your search query"
          />
        ) : (
          filteredConnections.map((connection) => {
            if (editingKey === connection.url) {
              return (
                <ExternalDataSourceEditor
                  key={connection.id}
                  name={connection.name}
                  url={connection.url}
                  onCancel={() => setEditingKey(null)}
                  onRemove={() => {
                    connectionsActions.removeOne(connection.id);
                    setEditingKey(null);
                  }}
                  onSave={(inputs) => {
                    connectionsActions.updateOne(connection.id, inputs);
                    setEditingKey(null);
                  }}
                />
              );
            }

            if (connection.deleted === true) {
              return (
                <Banner
                  key={connection.id}
                  type="warning"
                  title="Removed external connection"
                  description={
                    <span>
                      Connection <strong>“{connection.name}”</strong> to DBMS at {connection.url} was removed.
                    </span>
                  }
                  actions={[
                    {
                      label: 'Undo',
                      onClick: () => connectionsActions.restoreOne(connection.id),
                      fill: 'outlined',
                    },
                    {
                      label: 'Dismiss',
                      onClick: () => connectionsActions.purgeOne(connection.id),
                    },
                  ]}
                />
              );
            }

            return (
              <ExternalDataSourceCard
                key={connection.id}
                actions={
                  activeConnection.metadata?.id === connection.id && activeConnection.status.isConnected ? (
                    <Button
                      color="danger"
                      isDisabled={editingKey !== null}
                      size="small"
                      onClick={() => {
                        void activeConnection.disconnect();
                      }}
                    >
                      Disconnect
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      {props.allowEdit === true ? (
                        <Button
                          color="neutral"
                          fill="outlined"
                          isDisabled={editingKey !== null}
                          size="small"
                          onClick={() => {
                            setEditingKey(connection.url);
                          }}
                        >
                          Edit
                        </Button>
                      ) : null}
                      <Button
                        isDisabled={editingKey !== null}
                        size="small"
                        onClick={() => {
                          const port = connection.port === undefined ? '' : `:${connection.port}`;
                          connectionFormModal.open({
                            error: undefined,
                            hideConnectionMethods: undefined,
                            initialValues: {
                              protocol: connection.protocol,
                              hostname: `${connection.hostname}${port}`,
                              username: connection.credentials.type === 'basic' ? connection.credentials.username : '',
                            },
                            instanceDetails: {
                              id: '',
                              name: connection.name,
                            },
                            source: { type: 'user' },
                            warning: undefined,
                          });
                        }}
                      >
                        Connect
                      </Button>
                    </div>
                  )
                }
                authType={connection.credentials.type}
                connectionUrl={connection.url}
                isDisabled={editingKey !== null}
                name={<Highlighter substring={filterQuery} value={connection.name} />}
              />
            );
          })
        )}
      </div>
    </div>
  );
}
